import { AgentUserPagingResponse, AgentUserPagingResponseBasePagingResponse } from '@/__generated';
import Spin from '@/components/Spin';
import { getFullName } from '@/helpers/string';
import { apiSdk } from '@/httpClient';
import React from 'react';
import { useForm } from 'react-hook-form';
import ReactPaginate from 'react-paginate';
import CreateUpdateAgentModal from './createUpdateAgentModal';
import BooleanTag from '@/components/App/BooleanTag';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { DEFAULT_PAGE_SIZE, UNKNOWN_ERROR_MESSAGE } from '@/config';
import { Trans, useTranslation } from 'react-i18next';
import NtaDataGrid, { NTADataGridColumnProps } from '@/components/NtaDataGrid/NtaDataGrid';
import { useHistory } from 'react-router-dom';
import { useGetUseRole } from '@/hooks/useGetUserRole';
import { AppUrls } from '@/urls';
import { handleApiUnKnowError } from '@/helpers/errorHandle';

/**  */
interface SearchParams {
  firstName?: string;
  /**  */
  lastName?: string;
  /**  */
  userName?: string;
  /**  */
  email?: string;
  /**  */
  role?: string;
  /**  */
  pageIndex?: number;
  /**  */
  pageSize?: number;
  /**  */
  orderBy?: string;
  /**  */
  orderByAsc?: boolean;
}

export default function AgentUsers() {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [searchParams, setSearchParams] = React.useState<SearchParams>({
    pageSize: DEFAULT_PAGE_SIZE,
    pageIndex: 1,
    orderBy: 'firstName',
    orderByAsc: true,
  });
  const [agentUsersResponse, setAgentUsersResponse] = React.useState<AgentUserPagingResponseBasePagingResponse>();
  const [displayCreateUpdateModal, setDisplayCreateUpdateModal] = React.useState<boolean>(false);
  const [editingUser, setEditingUser] = React.useState<AgentUserPagingResponse>();
  const { register, handleSubmit, reset } = useForm();
  const history = useHistory();
  const { isAdmin } = useGetUseRole();
  if (!isAdmin) {
    history.push(AppUrls.Page401);
    return null;
  }
  const onSearch = values => {
    setSearchParams({
      pageSize: DEFAULT_PAGE_SIZE,
      pageIndex: 1,
      orderBy: 'firstName',
      orderByAsc: true,
      ...values,
    });
  };

  const getData = () => {
    setLoading(true);
    apiSdk.AgentUserService.getAgentUsers({ body: searchParams })
      .then(res => {
        setAgentUsersResponse(res);
      })
      .catch(handleApiUnKnowError)
      .finally(() => {
        setLoading(false);
      });
  };
  const onPageChange = (selectedItem: { selected: number }) => {
    setSearchParams(s => ({
      ...s,
      pageIndex: selectedItem.selected + 1,
    }));
  };
  React.useEffect(() => {
    getData();
  }, [searchParams]);

  const columns = React.useMemo(() => {
    return [
      {
        key: 'firstName',
        name: <Trans>base.FullName</Trans>,
        renderCell: ({ row }) => <b>{getFullName(row.firstName, row.lastName)}</b>,
        frozen: true,
      },
      {
        key: 'userName',
        name: <Trans>base.UserName</Trans>,
        frozen: true,
        width: 150,
      },
      {
        key: 'email',
        name: <Trans>base.Email</Trans>,
        width: 250,
      },
      {
        key: 'phoneNumber',
        name: <Trans>base.PhoneNumber</Trans>,
        width: 150,
      },
      {
        key: 'roles',
        name: <Trans>base.Roles</Trans>,
        renderCell: ({ row }) => <span className="fw-500">{row.roles?.join(', ')}</span>,
      },
      {
        key: 'active',
        name: <Trans>base.Active</Trans>,
        renderCell: ({ row }) => <BooleanTag value={row.active} />,
        width: 120,
      },
      {
        key: 'id',
        name: <Trans>base.Edit</Trans>,
        width: 120,
        renderCell: ({ row }) => (
          <button
            onClick={() => {
              setEditingUser(row);
              setDisplayCreateUpdateModal(true);
            }}
            className="text-center text-14 fw-500 text-blue-1"
          >
            <i className="icon-edit text-12" /> {t('base.Edit')}
          </button>
        ),
      },
    ] as NTADataGridColumnProps<AgentUserPagingResponse>[];
  }, []);
  return (
    <div>
      <div className="row y-gap-20 justify-between items-end pb-10 lg:pb-40 md:pb-32">
        <div className="col-12">
          <h1 className="text-30 lh-14 fw-600">{t('agentUserPage_Title')}</h1>
          <div className="text-15 text-light-1">{t('agentUserPage_SubTitle')}</div>
        </div>
      </div>
      {/* search form */}
      <form className="row mb-10 x-gap-5" onSubmit={handleSubmit(onSearch)}>
        <div className="col-2">
          <input {...register('firstName')} className="form-control omega-input" placeholder={t('base.FirstName') as any} />
        </div>
        <div className="col-2">
          <input {...register('lastName')} className="form-control omega-input" placeholder={t('base.LastName') as any} />
        </div>
        <div className="col-2">
          <input {...register('userName')} className="form-control omega-input" placeholder={t('base.UserName') as any} />
        </div>
        <div className="col-2">
          <input {...register('email')} className="form-control omega-input" placeholder={t('base.Email') as any} />
        </div>
        <div className="col d-flex">
          <button type="submit" className="button py-10  bg-blue-1 text-white  px-10">
            <i className="icon-search mr-5" /> {t('base.Search')}
          </button>
          <button type="button" className="button py-10  bg-red-2 text-white  px-10 ml-5" onClick={() => reset()}>
            <i className="icon-close text-12 mr-5" /> {t('base.Reset')}
          </button>
        </div>
        <div className="col-auto">
          <button
            className="button py-10  bg-blue-1 text-white  px-30"
            type="button"
            onClick={() => {
              setDisplayCreateUpdateModal(true);
            }}
          >
            <i className="icon-plus mr-15 " /> {t('agentUserPage_AddUser')}
          </button>
        </div>
      </form>

      <div className="py-30 px-30 rounded-4 bg-white shadow-3">
        <Spin loading={loading}>
          <NtaDataGrid<AgentUserPagingResponse>
            key={'id'}
            columns={columns}
            rows={agentUsersResponse?.data || []}
            columnsKeyConfig="agent-users"
            totalRecord={agentUsersResponse?.total}
            pageIndex={agentUsersResponse?.pageIndex}
            pageSize={agentUsersResponse?.pageSize}
            onPageChange={onPageChange}
          />
        </Spin>
      </div>
      {displayCreateUpdateModal ? (
        <CreateUpdateAgentModal
          user={editingUser}
          onClose={reload => {
            setDisplayCreateUpdateModal(false);
            setEditingUser(undefined);
            if (reload) {
              getData();
            }
          }}
        />
      ) : null}
    </div>
  );
}
